import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import {
  cancelCase,
  fieldErrorsOf,
  orderMutationErrorMessage,
  useCancelCase,
  type CancelEndpoint,
  type CaseMutationResultDto,
} from "../../../api/caseMutations.js";
import { Button } from "../../../ui/Button.js";
import { Dialog, DialogActions } from "../../../ui/Dialog.js";
import { FormField } from "../../../ui/FormField.js";
import { reasonError } from "../order-form/OrderForm.js";

/**
 * Cancelling a whole case (spec §2 "cancel a case", §6 "Cancelled: Parts
 * explicitly cancels the entire case with a reason", §17 "Records are
 * cancelled … never hard deleted" and "Destructive … actions require
 * confirmation").
 *
 * Two deliberate steps: the typed 5–500 character reason, then a second,
 * explicit confirmation that names the order and repeats the reason. The
 * request carries the version the dialog opened with; a newer version is
 * refused by the server and reported, never overridden. Nothing is deleted:
 * the case, its lines and its history stay on record as Cancelled.
 *
 * The same dialog cancels a wholesale order (wholesale spec §4.4): handed
 * that order's `endpoint` it posts to the wholesale route, and with no
 * `vehicleLabel` the confirmation names the order and the customer alone.
 */

export interface CancelCaseDialogProps<R = CaseMutationResultDto> {
  open: boolean;
  onClose: () => void;
  caseId: string;
  caseNumber: string;
  /** The vehicle line of the confirmation; a wholesale order has none. */
  vehicleLabel?: string | null;
  customerName: string;
  /** Frozen for the life of the dialog. */
  version: number;
  /** Where the cancellation is posted; defaults to the case route for `caseId`. */
  endpoint?: CancelEndpoint<R>;
  onCancelled: (result: R) => void;
}

export function CancelCaseDialog<R = CaseMutationResultDto>(props: CancelCaseDialogProps<R>) {
  if (!props.open) return null;
  return <CancelCaseFlow {...props} />;
}

type Step = "reason" | "confirm";

function CancelCaseFlow<R>({
  onClose,
  caseId,
  caseNumber,
  vehicleLabel = null,
  customerName,
  endpoint,
  onCancelled,
  ...live
}: CancelCaseDialogProps<R>) {
  const [version] = useState(live.version);
  // Without an endpoint the dialog works the case route, whose result is the default `R`.
  const [api] = useState(
    () => endpoint ?? (((input) => cancelCase(caseId, input)) as CancelEndpoint as unknown as CancelEndpoint<R>),
  );
  const [step, setStep] = useState<Step>("reason");
  const [reason, setReason] = useState("");
  const [reasonProblem, setReasonProblem] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const reasonId = useId();
  const keepRef = useRef<HTMLButtonElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const cancel = useCancelCase(api);
  const pending = cancel.isPending;

  // The dialog places focus once, on open; each step change moves it
  // deliberately — to the way back on the confirmation, to the reason when
  // returning — so a keyboard never lands nowhere.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    (step === "confirm" ? backRef.current : reasonRef.current)?.focus();
  }, [step]);

  const continueToConfirm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const problem = reasonError(reason) ?? null;
    setReasonProblem(problem);
    if (problem) {
      reasonRef.current?.focus();
      return;
    }
    setFormError(null);
    setStep("confirm");
  };

  const confirm = () => {
    if (pending) return;
    setFormError(null);
    cancel.mutate(
      { reason: reason.trim(), expectedVersion: version },
      {
        onSuccess: (result) => {
          onCancelled(result);
          onClose();
        },
        onError: (error) => {
          const fieldErrors = fieldErrorsOf(error);
          const reasonMessage = fieldErrors?.["reason"]?.find((text) => text.length > 0);
          if (reasonMessage) {
            setReasonProblem(reasonMessage);
            setStep("reason");
            return;
          }
          setFormError(orderMutationErrorMessage(error));
        },
      },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="Cancel this order?"
      description={`${caseNumber} stays on record as Cancelled with your reason in the activity history — nothing is deleted. Once cancelled it can't be edited or verified again.`}
      tone="destructive"
      pending={pending}
      size="small"
      initialFocusRef={step === "reason" ? keepRef : backRef}
    >
      {step === "reason" ? (
        <form noValidate onSubmit={continueToConfirm} className="cancel-form">
          <FormField id={reasonId} label="Cancellation reason" error={reasonProblem}>
            {(control) => (
              <textarea
                {...control}
                ref={reasonRef}
                className="text-input textarea-input"
                data-size="desktop"
                rows={3}
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value);
                  if (reasonProblem) setReasonProblem(null);
                }}
              />
            )}
          </FormField>
          <DialogActions>
            <Button ref={keepRef} variant="secondary" onClick={onClose}>
              Keep order
            </Button>
            <Button type="submit" variant="primary">
              Continue
            </Button>
          </DialogActions>
        </form>
      ) : (
        <div className="cancel-confirm">
          <dl className="cancel-confirm__facts">
            <div className="cancel-confirm__fact">
              <dt>Order</dt>
              <dd>{caseNumber}</dd>
            </div>
            {vehicleLabel !== null ? (
              <div className="cancel-confirm__fact">
                <dt>Vehicle</dt>
                <dd>{vehicleLabel}</dd>
              </div>
            ) : null}
            <div className="cancel-confirm__fact">
              <dt>Customer</dt>
              <dd>{customerName}</dd>
            </div>
            <div className="cancel-confirm__fact">
              <dt>Reason</dt>
              <dd>{reason.trim()}</dd>
            </div>
          </dl>
          <p className="cancel-confirm__note">
            The order leaves every active list and can't be changed afterwards. This can't be undone.
          </p>
          {formError ? (
            <p role="alert" className="form-alert">
              {formError}
            </p>
          ) : null}
          <DialogActions>
            <Button ref={backRef} variant="secondary" onClick={() => setStep("reason")} disabled={pending}>
              Back
            </Button>
            <Button variant="destructive" onClick={confirm} busy={pending}>
              Yes, cancel this order
            </Button>
          </DialogActions>
        </div>
      )}
    </Dialog>
  );
}
